import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router";
import FormManager from "../../../../utility/features/form_managers/account/easyPayEnrollForm";
import ViewPaymentMethodsClient from "../../../../utility/apiClients/customer/ViewPaymentMethodsClient";
import customerSummaryClient from "../../../../utility/apiClients/customer/customerSummaryClient";
import NoEasyPayService from "../../accountSettings/easypay-settings/noEasyPayServices";
import Globals, { URLS } from "../../../../utility/constants/appConstants";
import { formatDateMMDDYYYY, formatDateYYYYMMDD } from "../../../../utility/utils/dateUtil";
import {
  PrepayNewPaymentMethodV2,
  PrepayExistingPaymentMethodV2,
} from "../../../../utility/apiClients/account/prePayOTPaymentClient";
import paymentStyles from "../../../../styles/Payments/easyPayPayment.module.scss";
import { customerServiceNumberFormatted } from '../../../../utility/utils/formatter';
import tempJson from "../../../../json/fallbackglobalvariables.json";
import SelectionBlock from "../../accountDetailsSharedBlocks/SelectionBlock";
import PaymentBlock from "../../accountDetailsSharedBlocks/PaymentBlock";
import { setCustomerDetails } from "../../../../reduxStore/myAccount/getCustomerDetails";
import { Link } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { setCustomerInfo } from "../../../../reduxStore/login/loginSlice";
import Loader from "../../../loader";
import { easyPayCalculate } from "../../../../utility/features/helpers/accountHelper";
import accountDetails from "../../../../utility/apiClients/account/accountDetailsClient";
import {setBypassAccountDetailsCache} from "../../../../reduxStore/myAccount/accountSummarySlice";
import ErrorBlock from '../../../errorMessage';
import missionStatusClient from '../../../../utility/apiClients/user/missionStatusClient';

const EasyPayPayment = (props) => {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const [form] = useState(FormManager.createForm());

  const selectedInvoiceRef = useRef(null);
 
  const [error, setErrorMsg] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [success, setSuccess] = useState(false);
  const [successProps, setSuccessProps] = useState({});
  const [isPaymentSubmitted, setIsPaymentSubmitted] = useState(false);
  const [selectionData, SetSelectionData] = useState([]);
  const [totalPrePayAmount] = useState(0.0);
  const [isLoading, setIsLoading] = useState({
      isCustomerSummaryApiLoaded : false,
      isSaleAgreementApiLoaded : false,
      isPaymentMethodApiLoaded : false
  });
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isLoadingSelectionBlock, setIsLoadingSelectionBlock] = useState(false);
  const [paymentData, setPaymentData] = useState([]);
  const [hasSelectedService, setHasSelectedService] = useState(false);
  const customerSummary = useSelector((state) => state.login.customerInfo);
  const [hasSelection, setHasSelection] = useState(false);
  const selectedServiceAddress = useSelector(
    (state) => state.accountSummary.selectedServiceAddress
  );
  const allCustomersApiCalled = useSelector((state) => state.login.allCustomersApiCalled);
  const custNumbers = useMemo(() => JSON.parse(localStorage.getItem("customerNumbers")) || [], []);
  
  const [serviceAddress, customerNumber] = useMemo(() => {
    let custNumber = "";
    let serviceAddr = "";
    if (selectedServiceAddress !== null) {
      [serviceAddr, custNumber] = selectedServiceAddress.split(" | ");
    } else {
      custNumber = customerSummary ? customerSummary?.[0]?.CustomerNumber : custNumbers[0];
      serviceAddr = customerSummary ? customerSummary?.[0]?.ServiceAddresses?.[0]?.address
        : "";
    }
    return [serviceAddr, custNumber];
  }, [selectedServiceAddress, customerSummary]);
  
  const { customerDetails = {} } = useSelector(
    (state) => state.getCustomerDetails || {}
  );
  
 
  const labels = useMemo(() => tempJson.acc, []);

  const missionStatusCheck = async () => {
    try {
      const missionStatus = await missionStatusClient();
      return !["yes"].includes(String(missionStatus?.data).trim().toLowerCase());
    } catch (err) {
      console.error("Error checking mission status api:", err);
      return false;
    }
  };

  const getCustomerSummary = async (custNum) => {
    if (custNum === "") return;
    setErrorMsg("");

    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      setIsLoading((prev) => ({ ...prev, isCustomerSummaryApiLoaded: false }));
      return;
    }

    setIsLoading((prev) => ({
      ...prev,
      isCustomerSummaryApiLoaded: false
    }));
    try {
      const customerSummary = await customerSummaryClient(custNum);
      if (customerSummary?.status && customerSummary?.status === 401 ) {
        console.log("Unauthorized access - 401");
        localStorage.clear();
        nav("/login", { replace: true });
        return;
      }
      
      if (
        customerSummary?.data === null ||
        customerSummary?.data === undefined
      ) {
        setErrorMsg("Some error occurred.");
        return;
      }
      dispatch(setCustomerInfo(customerSummary?.data));
    } catch (err) {
      setErrorMsg("Some error occurred.");
    } finally {
      setIsLoading((prev) => ({
        ...prev,
        isCustomerSummaryApiLoaded: true
      }));
    }
  };

  const fetchSalesAgg = async () => {
    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      setIsLoading((prev) => ({ ...prev, isSaleAgreementApiLoaded: false }));
      return;
    }

    setIsLoading((prev) => ({
      ...prev,
      isSaleAgreementApiLoaded: false
    }));
    try {
      const response = await accountDetails(customerNumber ?? custNumbers[0], serviceAddress);
      if (response?.SalesAgreements) {
        const { easyPaySvcAg, nonEasyPaySvcAg } = easyPayCalculate(response.SalesAgreements);
        SetSelectionData(nonEasyPaySvcAg);
        dispatch(setCustomerDetails(response));
      } else if (response?.errorStatus === 424) {
        setErrorMsg("Some error occurred.");
      } 
    }
    catch(error) {
      setErrorMsg("Some error occurred.");
      console.error("Error fetching sales agreements:", error)
    }
    finally {
      //setIsLoadingSelectionBlock(false);
      setIsLoading((prev) => ({
        ...prev,
        isSaleAgreementApiLoaded: true
      }));
    }
  };

  const fetchPaymentServices = async () => {
    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      setIsLoading((prev) => ({ ...prev, isPaymentMethodApiLoaded: false }));
      return;
    }

    setIsLoading((prev) => ({
      ...prev,
      isPaymentMethodApiLoaded: false
    }));
    try {
      const paymentInfo = await ViewPaymentMethodsClient({
        cusNum: customerNumber ?? custNumbers[0],
      });

      const methods = paymentInfo?.data?.PaymentMethods || [];

      setPaymentData(methods);
    } catch (error) {
      setErrorMsg("Some error occurred.");
      console.error("Error fetching payment methods:", error);
      setPaymentData([]);
    } finally {
      setIsLoading((prev) => ({
        ...prev,
        isPaymentMethodApiLoaded: true
      }));
    }
  };

  useEffect(() => {
    if (customerNumber || custNumbers.length > 0) {
      (async () => {
        await getCustomerSummary(customerNumber);

        /* sales agreement */
        if (props.nonEasyPaySvcAg) {
          const aggDataArray = Array.isArray(props?.aggData) ? props.aggData : [];

          const data = props?.nonEasyPaySvcAg?.filter((val) =>
            aggDataArray.includes(String(val.salesAgrNum))
          );
          SetSelectionData(data);
        } else {
          if(allCustomersApiCalled)
            await fetchSalesAgg();
        }

        await fetchPaymentServices();
      })();
    }

    return () => {
      setErrorMsg("");
      setIsLoading({
        isCustomerSummaryApiLoaded : false,
        isSaleAgreementApiLoaded : false,
        isPaymentMethodApiLoaded : false
      });
    }
  }, [selectedServiceAddress, allCustomersApiCalled]);

  useEffect(() => {
    const selectedServices = form["salesAgreementNumbers"]?.value || [];
    setHasSelectedService(selectedServices.length > 0);
  }, [form, refresh]);

  const makeSalesAgreementObj = (salesAgreements) => {
    let value = [];
    let data = {};
    salesAgreements.forEach((sa) => {
      if(selectedInvoiceRef.current !== null && selectedInvoiceRef.current?.[sa.salesAgrNum] === false)
        return;
      
      if(selectedInvoiceRef.current !== null && !Object.keys(selectedInvoiceRef.current).includes(sa.salesAgrNum))
        return;        

      value.push(sa.salesAgrNum);
      data = {...data, [sa.salesAgrNum]: sa };
    });

    return { value, data };
  }

  const handleSubmit = async (paymentData, isNew) => {
    setIsLoading(true);
    setErrorMsg("");
    setIsPaymentSubmitted(true);

    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(Globals.acc.missionDownErrorDisplay);
      setIsLoading(false);
      window.scrollTo(0, 0);
      return;
    }

    const salesAgreementObj = makeSalesAgreementObj(selectionData);

    if (isNew) {
      try {
        const response = await PrepayNewPaymentMethodV2(
          isNew,
          "easypay",
          paymentData,
          customerNumber,
          salesAgreementObj,
          ""
        );

        if(typeof response.error === "undefined"){

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "account_action",
            category: "Account",
            action: "payments",
            label: `prepay initiated - ${
              paymentData.isCreditCard ? "Card" : "Account"
            }`,
            customer_id: customerNumber || {},
            error: {
              reason: "",
            },
          });

          setSuccessProps({
            cashSubTyp:
              paymentData.isCreditCard && !paymentData.isAccount
                ? paymentData.cashSubType
                : "CHK",
            lastFour:
              paymentData.isCreditCard && !paymentData.isAccount
                ? paymentData["Card Number"].slice(-4)
                : paymentData["Account Number"].slice(-4),
            salesAgreements: salesAgreementObj,
            confNum: response,
          });

          setSuccess(true);
          dispatch(setBypassAccountDetailsCache(true));

          let checkedData = sessionStorage.getItem("paymentlist");
          let parseList = JSON.parse(checkedData);
          if (paymentData.isCreditCard && !paymentData.isAccount) {
            parseList[0].successData = {
              method: paymentData.cashSubType,
              confirmation: response,
              data: {
                pccAcctLast4: paymentData["Card Number"].slice(-4),
              },
            };
          } else {
            parseList[0].successData = {
              method: "CHK",
              confirmation: response,
              data: {
                pccAcctLast4: paymentData["Account Number"].slice(-4),
              },
            };
          }

          sessionStorage.setItem("paymentlist", JSON.stringify(parseList));

          sessionStorage.setItem(
            "epsuccessProps",
            JSON.stringify({
              cashSubTyp:
                paymentData.isCreditCard && !paymentData.isAccount
                  ? paymentData.cashSubType
                  : "CHK",
              lastFour:
                paymentData.isCreditCard && !paymentData.isAccount
                  ? paymentData["Card Number"].slice(-4)
                  : paymentData["Account Number"].slice(-4),
              salesAgreements: salesAgreementObj,
              confNum: response,
            })
          );

          return response;
        } else {
          setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
          window.scrollTo(0, 0);
        }
      } catch (error) {
        
        if(error?.genericMessage)
          setErrorMsg(error?.genericMessage);
        else
          setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
        window.scrollTo(0, 0);
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const response = await PrepayExistingPaymentMethodV2(
          isNew,
          "easypay",
          paymentData,
          customerNumber,
          salesAgreementObj,
          ""
        );
        
        if(typeof response.error === "undefined"){
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "account_action",
            category: "Account",
            action: "payments",
            label: `enrollment initiated - ${paymentData.pccCashType === 'CCD' ? 'Card' : 'Account'}`,
            customer_id: customerNumber || {},
            error: {
              reason: "",
            },
          });

          setSuccessProps({
            cashSubTyp: paymentData.pccCashSubType,
            lastFour: paymentData.pccAcctLast4,
            salesAgreements: salesAgreementObj,
            confNum: response,
          });

          setSuccess(true);
          dispatch(setBypassAccountDetailsCache(true));

          let checkedData = sessionStorage.getItem("paymentlist");
          let parseList = JSON.parse(checkedData);

          if (paymentData.pccCashType!=="CHK") {
            parseList[0].successData = {
              method: paymentData.pccCashSubType,
              confirmation: response,
              data: {
                pccAcctLast4: paymentData.pccTokenNum.slice(-4),
              },
            };
          } else {
            parseList[0].successData = {
              method: "CHK",
              confirmation: response,
              data: {
                pccAcctLast4: paymentData.pccTokenNum.slice(-4),
              },
            };
          }

          sessionStorage.setItem("paymentlist", JSON.stringify(parseList));

          sessionStorage.setItem(
            "epsuccessProps",
            JSON.stringify({
              cashSubTyp: paymentData.pccCashSubType,
              lastFour: paymentData.pccAcctLast4,
              salesAgreements: salesAgreementObj,
              confNum: response,
            })
          );

          return response;
        } else {
          setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
          window.scrollTo(0, 0);
        }
      } catch (error) {
        setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
        window.scrollTo(0, 0);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
    <style>
      {`
       .font-italic {
            font-style: italic;
        }
        .gfw-7{
          font-weight:700;
        }
      `}
    </style>

      {error != "" && <ErrorBlock labels={labels} errMsg={error} />}
     
      {error == "" && <div class="bg-green d-md-none my-20 py-20 row d-md-none">
        <div class="col-12 gfw-3 glh-1_1 text-white tru-mma-mobile-current">
          <span class="d-block gfs-30 mb-6">Payments</span>
          <span class="d-block gfs-20">
            Pay your balance or view your payment history.
          </span>
        </div>
      </div>}
     
      {error == "" && !success && (
        <div
          className={`${paymentStyles["bg-gray-100"]} mb-md-40  ${paymentStyles["p-30"]} ${paymentStyles["tru-mma-main-wrapper"]} tru-mma-mobile-margins tru-mma-recent-payments`}
        >
          <div className="container-fluid">
            <div className={`${paymentStyles["mb-20"]} row`}>
              <div
                className={`col-12 ${paymentStyles["gfw-3"]} p-0 text-center`}
              >
                <span
                  className={`gfs-24 ${paymentStyles["gfs-lg-30"]} ${paymentStyles["glh-1_2"]} mb-10 ${paymentStyles["text-gray-med"]}`}
                  dangerouslySetInnerHTML={{ __html: labels.easyPayEnrolled }}
                />&nbsp;
                <Link
                  to={URLS.easyPaySettings}
                  className={`${paymentStyles["bg-gray-300"]} ${paymentStyles["gfs-20"]} ${paymentStyles["glh-1_2"]} d-block d-lg-inline  ${paymentStyles["gfw-3"]} mt-10 mt-lg-0 ${paymentStyles["px-20"]} ${paymentStyles["py-5"]} ${paymentStyles["ep-setting-btn"]} text-white`}
                  dangerouslySetInnerHTML={{
                    __html: labels.manageEasyPaySettings,
                  }}
                />
              </div>
            </div>
            <div className={`${paymentStyles["mb-20"]} row`}>
              <div className={`col-12 ${paymentStyles["gfw-3"]} p-0`}>
                <span
                  className={`d-block gfs-24 ${paymentStyles["gfs-30"]} ${paymentStyles["glh-1_2"]} mb-10 ${paymentStyles["text-gray-med"]}`}
                  dangerouslySetInnerHTML={{ __html: labels.easyPaySetup }}
                />
                <span className={`d-block pt-3 ${paymentStyles["gfs-20"]}`}>
                  {labels.descriptionEE}
                </span>
                <span
                  className={`d-block ${paymentStyles["gfs-20"]}`}
                  dangerouslySetInnerHTML={{ __html: labels.serviceChoiceEE }}
                />
              </div>
            </div>

            <div className="ShowLoadingModal" id="PaymentForm">
              { (isLoading.isSaleAgreementApiLoaded && isLoading.isPaymentMethodApiLoaded) && selectionData?.length > 0 ? (
                <>
                  <SelectionBlock
                    paymentData={selectionData}
                    setHasSelection={setHasSelection}
                    ref={selectedInvoiceRef}
                    isPaymentSubmitted={isPaymentSubmitted}
                  />

                  {hasSelection &&
                  <PaymentBlock
                    form={form}
                    labels={labels}
                    totalAmountPrePay={totalPrePayAmount}
                    paymentDataProp={paymentData}
                    handleDataSubmit={handleSubmit}
                    hasSelection={hasSelection}
                    flag={""}
                  />}
                </>
              ) : (
                (!isLoading.isSaleAgreementApiLoaded || !isLoading.isPaymentMethodApiLoaded) ? (
                  <Loader />
                ) : <NoEasyPayService labels={labels} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EasyPayPayment;
